import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../services/api';
import { getSafeErrorMessage } from '../../dashboard/dashboardUtils';
import { 
  FaTimes, 
  FaKey, 
  FaExclamationTriangle, 
  FaFileImport, 
  FaDownload, 
  FaCheckCircle, 
  FaCopy
} from 'react-icons/fa';
import * as Sentry from "@sentry/react";
import { useAuth } from '../../components/auth/AuthContext';

// Sentry logging helpers
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'ssh-key-modal' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'ssh-key-modal' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'ssh-key-modal',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// Error message constants
const ERROR_MESSAGES = {
  'NETWORK_ERROR': 'Unable to connect. Please check your internet connection',
  'SERVER_ERROR': 'Service temporarily unavailable. Please try again',
  'RATE_LIMIT_EXCEEDED': 'Too many requests. Please wait before trying again',
  'OFFLINE': "You're offline. Check your connection.",
  'TIMEOUT': "Request timed out. Please try again.",
  'SERVER_UNREACHABLE': "Network connection failed. Please check your internet connection and try again.",
  'NO_RESPONSE': "No response from server. Please try again.",
  'UNKNOWN_NETWORK_ERROR': "Something went wrong. Please try again."
};


const getNetworkErrorMessage = (error) => {
  if (!navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return {
      message: "Request timed out. Please try again.",
      code: 'TIMEOUT'
    };
  }
  
  if (error.code === 'ERR_NETWORK' && navigator.onLine) {
    return {
      message: "Unable to reach server. Please try later.",
      code: 'SERVER_UNREACHABLE'
    };
  }
  
  if (error.code === 'ERR_NETWORK' && !navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  if (error.request && !error.response) {
    return {
      message: "No response from server. Please try again.",
      code: 'NO_RESPONSE'
    };
  }
  
  return {
    message: "Something went wrong. Please try again.",
    code: 'UNKNOWN_NETWORK_ERROR'
  };
};
// Security: Input sanitization function
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

const sanitizeKeyName = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[^a-zA-Z0-9_\-]/g, '')
    .substring(0, 30);
};

const SSHKeyModal = ({ isOpen, type, onClose, onSave, existingKeyNames = [] }) => {
  const { handleAuthError } = useAuth();
  const [keyName, setKeyName] = useState('');
  const [publicKey, setPublicKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedKey, setGeneratedKey] = useState(null);
  const [nameCharCount, setNameCharCount] = useState(0);
  const [nameError, setNameError] = useState('');
  const [publicKeyError, setPublicKeyError] = useState('');
  const [copied, setCopied] = useState(false);
  const [selectedRegionForKey, setSelectedRegionForKey] = useState('');
  const [isRegionKeyDropdownOpen, setIsRegionKeyDropdownOpen] = useState(false);
  const [showRetry, setShowRetry] = useState(false);
  
  const keyNameInputRef = useRef(null);
  const publicKeyTextareaRef = useRef(null);
  const regionKeyDropdownRef = useRef(null);
  const modalRef = useRef(null);
  const abortControllerRef = useRef(null);
  const lastCreateAttemptRef = useRef(0);
  const CREATE_COOLDOWN = 2000;
  
  // Set Sentry context when modal opens or type changes
  useEffect(() => {
    if (isOpen) {
      Sentry.setContext("ssh-key-modal", {
        isOpen: true,
        type,
        keyName: keyName || 'empty',
        hasPublicKey: !!publicKey,
        hasError: !!error,
        hasGeneratedKey: !!generatedKey,
        selectedRegion: selectedRegionForKey || 'none'
      });
      
      logInfo('SSH key modal opened', {
        type,
        keyName: keyName || 'empty',
        hasPublicKey: !!publicKey,
        hasError: !!error,
        hasGeneratedKey: !!generatedKey
      });
    }
  }, [isOpen, type, keyName, publicKey, error, generatedKey, selectedRegionForKey]);
  
  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        logInfo('Cleanup: Aborted pending SSH key requests');
      }
    };
  }, []);
  
  // Rate limiting check
  const checkCreateRateLimit = () => {
    const now = Date.now();
    const timeSinceLastCreate = now - lastCreateAttemptRef.current;
    
    if (timeSinceLastCreate < CREATE_COOLDOWN) {
      logError('SSH key create attempt blocked - rate limit exceeded', {
        timeSinceLastCreate,
        cooldownPeriod: CREATE_COOLDOWN
      });
      return false;
    }
    
    lastCreateAttemptRef.current = now;
    return true;
  };
  
  // Key name validation - must be alphanumeric with hyphens and underscores, max 50 chars
  const validateKeyName = (name) => {
    if (!name.trim()) {
      return 'Key name is required';
    }
    
    if (name.length > 30) {
      return 'Key name must be 30 characters or less';
    }
    
if (!name.match(/^[a-zA-Z0-9_\-]+$/)) {
      return 'Key name can only contain letters, numbers, underscores, and hyphens';
    }
    
    return '';
  };
  
  // SSH public key format validation
  const validatePublicKey = (key) => {
    if (!key.trim()) {
      return 'Public key is required';
    }
    
    // Basic format check - starts with ssh-rsa, ssh-ed25519, etc.
    const validPrefixes = ['ssh-rsa', 'ssh-ed25519', 'ssh-dss', 'ecdsa-sha2-nistp256', 'ecdsa-sha2-nistp384', 'ecdsa-sha2-nistp521'];
    const keyParts = key.trim().split(' ');
    
    if (!keyParts.length) {
      return 'Invalid public key format';
    }
    
    if (!validPrefixes.includes(keyParts[0])) {
      return 'Invalid public key format. Key should start with ssh-rsa, ssh-ed25519, etc.';
    }
    
    return '';
  };
  
  // Handle key name changes with validation and sanitization
  const handleKeyNameChange = (e) => {
    const rawValue = e.target.value;
    const sanitizedValue = sanitizeKeyName(rawValue);
    setKeyName(sanitizedValue);
    setNameCharCount(sanitizedValue.length);
    const validationError = validateKeyName(sanitizedValue);
    if (validationError) {
      setNameError(validationError);
    } else if (existingKeyNames.includes(sanitizedValue.trim())) {
      setNameError('An SSH key with this name already exists');
      
      logInfo('SSH key name validation failed - duplicate name', {
        attemptedName: sanitizedValue.trim(),
        existingNames: existingKeyNames.length
      });
      
      logUserAction('ssh_key_name_validation_failed', {
        error: 'duplicate_name',
        nameLength: sanitizedValue.length
      });
    } else {
      setNameError('');
    }
    
    logUserAction('ssh_key_name_change', {
      type,
      keyName: sanitizedValue,
      charCount: sanitizedValue.length,
      hasValidationError: !!validationError,
      validationError: validationError || null
    });
    
    logInfo('SSH key name changed', {
      type,
      keyName: sanitizedValue,
      charCount: sanitizedValue.length,
      isValid: !validationError
    });
  };
  
  // Handle public key changes with validation (don't sanitize SSH keys as they need specific format)
  const handlePublicKeyChange = (e) => {
    const value = e.target.value;
    // SSH public keys are typically 68-724 characters. 2048 provides ample headroom.
    const MAX_KEY_LENGTH = 2048;
    
    if (value.length <= MAX_KEY_LENGTH) {
      setPublicKey(value);
      
      if (type === 'import') {
        const validationError = validatePublicKey(value);
        setPublicKeyError(validationError);
        
        logUserAction('ssh_public_key_change', {
          type,
          keyLength: value.length,
          hasValidationError: !!validationError,
          validationError: validationError || null,
          keyType: value.trim().split(' ')[0] || 'unknown'
        });
        
        logInfo('SSH public key changed', {
          type,
          keyLength: value.length,
          isValid: !validationError,
          keyType: value.trim().split(' ')[0] || 'unknown'
        });
      }
    } else {
      setPublicKeyError(`Public key exceeds maximum length of ${MAX_KEY_LENGTH} characters`);
      
      logUserAction('ssh_public_key_change_rejected', {
        type,
        attemptedLength: value.length,
        maxLength: MAX_KEY_LENGTH,
        reason: 'exceeds_max_length'
      });
      
      logInfo('SSH public key change rejected - too long', {
        type,
        attemptedLength: value.length,
        maxLength: MAX_KEY_LENGTH
      });
    }
  };

  // Toggle region key dropdown
  const toggleRegionKeyDropdown = () => {
    const newState = !isRegionKeyDropdownOpen;
    setIsRegionKeyDropdownOpen(newState);
    
    logUserAction('ssh_key_region_dropdown_toggle', {
      type,
      isOpen: newState,
      selectedRegion: selectedRegionForKey || 'none',
      availableRegions: generatedKey?.regionKeys ? Object.keys(generatedKey.regionKeys).length : 0
    });
  };

  // Handle region key selection
  const handleRegionKeySelect = (region) => {
    const sanitizedRegion = sanitizeText(region);
    setSelectedRegionForKey(sanitizedRegion);
    setIsRegionKeyDropdownOpen(false);
    
    logUserAction('ssh_key_region_selected', {
      type,
      selectedRegion: sanitizedRegion,
      previousRegion: selectedRegionForKey || 'none'
    });
    
    logInfo('SSH key region selected', {
      type,
      selectedRegion: sanitizedRegion,
      keyName
    });
  };

  // Get country flag (simplified - in a real app, you'd use a flag library)
  const getCountryFlag = (countryCode) => {
    // Simplified placeholder for flag display
    return <span>{sanitizeText(countryCode)}</span>;
  };
  
const handleCreate = async (isRetry = false) => {
  const startTime = performance.now();
    
  logUserAction('ssh_key_create_initiated', {
    type,
    keyName,
    hasPublicKey: !!publicKey,
    publicKeyLength: publicKey.length
  });
  
  logInfo('SSH key creation/import started', {
    type,
    keyName,
    hasPublicKey: !!publicKey,
    publicKeyLength: publicKey.length
  });
  
  // Check rate limit
  if (!isRetry && !checkCreateRateLimit()) {
    setError(ERROR_MESSAGES['RATE_LIMIT_EXCEEDED']);
    return;
  }
  
  // Validate key name
  const nameValidationError = validateKeyName(keyName);
  if (nameValidationError) {
    setNameError(nameValidationError);
    
    logError('SSH key validation failed: Invalid name', {
      type,
      keyName,
      validationError: nameValidationError
    });
    
    logUserAction('ssh_key_create_validation_failed', {
      type,
      reason: 'invalid_name',
      keyName
    });
    return;
  }

  // Check for duplicate names
  if (existingKeyNames.includes(keyName.trim())) {
    setNameError('An SSH key with this name already exists');
    
    logError('SSH key validation failed: Duplicate name', {
      type,
      keyName: keyName.trim(),
      existingNames: existingKeyNames.length
    });
    
    logUserAction('ssh_key_create_validation_failed', {
      type,
      reason: 'duplicate_name',
      keyName: keyName.trim()
    });
    return;
  }
  
  // For import type, validate public key format
  if (type === 'import') {
    const keyValidationError = validatePublicKey(publicKey);
    if (keyValidationError) {
      setPublicKeyError(keyValidationError);
      
      logError('SSH key validation failed: Invalid public key', {
        type,
        keyName,
        validationError: keyValidationError,
        publicKeyLength: publicKey.length,
        publicKeyType: publicKey.trim().split(' ')[0] || 'unknown'
      });
      
      logUserAction('ssh_key_create_validation_failed', {
        type,
        reason: 'invalid_public_key',
        keyName
      });
      return;
    }
  }
  
  if (abortControllerRef.current) {
    abortControllerRef.current.abort();
  }
  
  abortControllerRef.current = new AbortController();
  setLoading(true);
  setError(null);
  setShowRetry(false);
  
  try {
    logInfo('Making SSH key API request using API service', {
      type,
      keyName,
      hasPublicKey: type === 'import'
    });
    
    // Use API service instead of direct fetch
    const data = type === 'create' 
      ? await api.createSSHKey(keyName)
      : await api.importSSHKey(keyName, publicKey);
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (abortControllerRef.current?.signal.aborted) {
      return;
    }
    
    // Check for success in API response
    if (!data.success && !data.id) {
      throw new Error(data.error || 'Failed to process SSH key');
    }
    
    logPerformance(`ssh_key_${type}`, duration, {
      keyName,
      hasPrivateKey: !!data.private_key,
      hasPublicKey: !!data.public_key,
      hasRegionKeys: !!(data.legacy_regions && Object.keys(data.legacy_regions).length > 0),
      regionCount: data.legacy_regions ? Object.keys(data.legacy_regions).length : 0,
      success: true
    });
    
    logInfo('SSH key creation/import completed successfully', {
      type,
      keyName,
      keyId: data.id,
      hasPrivateKey: !!data.private_key,
      hasPublicKey: !!data.public_key,
      regionCount: data.legacy_regions ? Object.keys(data.legacy_regions).length : 0,
      duration: `${duration.toFixed(2)}ms`
    });
    
    // If this is a newly created key, we'll receive the private key - store it and trigger download
    if (type === 'create' && data.private_key) {
      setGeneratedKey({
        privateKey: data.private_key,
        publicKey: data.public_key,
        regionKeys: data.legacy_regions || {}
      });
      
      logInfo('SSH key generated, starting private key download', {
        keyName,
        keyId: data.id,
        privateKeyLength: data.private_key.length,
        regionCount: data.legacy_regions ? Object.keys(data.legacy_regions).length : 0
      });
      
      // Automatically download the private key
      const element = document.createElement('a');
      const file = new Blob([data.private_key], {type: 'text/plain'});
      element.href = URL.createObjectURL(file);
      const safeKeyName = sanitizeKeyName(keyName);
      element.download = `${safeKeyName}_private_key.pem`;
      document.body.appendChild(element);
      element.click();
      URL.revokeObjectURL(element.href);
      document.body.removeChild(element);
      
      logUserAction('ssh_private_key_downloaded', {
        keyName,
        keyId: data.id,
        filename: `${keyName}_private_key.pem`
      });
    }
    
    // Call the onSave with the new key data including all fields from the response
    onSave({
      id: data.id,
      name: data.name || keyName,
      publicKey: data.public_key || publicKey,
      fingerprint: data.fingerprint,
      createdAt: data.created_at,
      regionKeys: data.legacy_regions
    });
    
    logUserAction('ssh_key_create_success', {
      type,
      keyName,
      keyId: data.id
    });
    
    // If this is an import, close the modal after successful save
    if (type === 'import') {
      logInfo('SSH key import completed, closing modal', {
        keyName,
        keyId: data.id
      });
      onClose();
    }
    
  } catch (err) {
    if (err.name === 'AbortError') {
      return;
    }
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.status === 401) {
      handleAuthError();
      return;
    }
    
    // Position 2: Network error
    const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
    if (networkErrorCodes.includes(err.code)) {
      setError(err.error);
      setShowRetry(true);
      logError('Network error in handleCreate', { 
        code: err.code,
        duration: `${duration.toFixed(2)}ms`,
        type,
        keyName
      }, err);
      
      logUserAction('ssh_key_create_failed', {
        type,
        keyName,
        errorType: err.code
      });
      return;
    }
    
    // Position 3: Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
      setError(err.error);
      logError('SSH key creation rate limited', {
        status: err.status,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      
      logUserAction('ssh_key_create_failed', {
        type,
        keyName,
        errorType: 'rate_limit',
        status: err.status
      });
      return;
    }
    
    // Position 4: Server errors (500+)
    if (err.status >= 500) {
      setError(ERROR_MESSAGES['SERVER_ERROR']);
      setShowRetry(true);
      logError('SSH key creation server error', {
        status: err.status,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      
      logUserAction('ssh_key_create_failed', {
        type,
        keyName,
        errorType: 'server_error',
        status: err.status
      });
      return;
    }
    
    // Position 5: Mapped error code
    if (err.code && ERROR_MESSAGES[err.code]) {
      setError(ERROR_MESSAGES[err.code]);
      setShowRetry(true);
      logError('SSH key creation failed - mapped code', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`,
        type,
        keyName
      }, err);
      
      logUserAction('ssh_key_create_failed', {
        type,
        keyName,
        errorType: err.code
      });
      return;
    }
    
    // Position 6: Fallback
    const safeErrorMessage = err.error || getSafeErrorMessage(err);
    setError(safeErrorMessage);
    
    logError('SSH key creation/import failed', {
      type,
      keyName,
      errorName: err.name,
      duration: `${duration.toFixed(2)}ms`,
      hasPublicKey: !!publicKey,
      publicKeyLength: publicKey.length
    }, err);
    
    logUserAction('ssh_key_create_failed', {
      type,
      keyName,
      errorMessage: safeErrorMessage
    });
  } finally {
    setLoading(false);
  }
};  
  // Retry function for network/server errors
const handleRetry = () => {
  logUserAction('ssh_key_create_retry_initiated', {
    type,
    keyName,
    hasError: !!error
  });
  
  logInfo('Retrying SSH key creation/import', {
    type,
    keyName,
    previousError: error
  });
  
  setError(null);
  setShowRetry(false);
  handleCreate(true);
};  
  // Handle download private key
 const handleDownloadKey = () => {
  if (!generatedKey) {
    logError('Download attempted without generated key', {
      type,
      keyName,
      hasGeneratedKey: !!generatedKey
    });
    return;
  }
  
  logUserAction('ssh_private_key_download_initiated', {
    type,
    keyName,
    privateKeyLength: generatedKey.privateKey.length
  });
  
  const element = document.createElement('a');
  const file = new Blob([generatedKey.privateKey], {type: 'text/plain'});
  element.href = URL.createObjectURL(file);
  const safeKeyName = sanitizeKeyName(keyName);
  element.download = `${safeKeyName}_private_key.pem`;
  document.body.appendChild(element);
  element.click();
  URL.revokeObjectURL(element.href);
  document.body.removeChild(element);
    
    logInfo('SSH private key downloaded successfully', {
      type,
      keyName,
      filename: `${keyName}_private_key.pem`,
      privateKeyLength: generatedKey.privateKey.length
    });
    
    logUserAction('ssh_private_key_download_success', {
      type,
      keyName,
      filename: `${keyName}_private_key.pem`
    });
  };
  
  // Function to copy key to clipboard
  const handleCopyKey = (text) => {
    const startTime = performance.now();
    
    logUserAction('ssh_key_copy_initiated', {
      type,
      keyName,
      textLength: text.length,
      selectedRegion: selectedRegionForKey || 'none'
    });
    
    navigator.clipboard.writeText(text).then(() => {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      
      logPerformance('ssh_key_copy', duration, {
        type,
        keyName,
        textLength: text.length,
        success: true
      });
      
      logInfo('SSH key copied to clipboard successfully', {
        type,
        keyName,
        textLength: text.length,
        selectedRegion: selectedRegionForKey || 'none',
        duration: `${duration.toFixed(2)}ms`
      });
      
      logUserAction('ssh_key_copy_success', {
        type,
        keyName,
        textLength: text.length
      });
    }).catch(error => {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      setError('Failed to copy to clipboard');
      
      logError('SSH key copy failed', {
        type,
        keyName,
        textLength: text.length,
        errorName: error.name,
        errorMessage: error.message,
        duration: `${duration.toFixed(2)}ms`
      }, error);
      
      logUserAction('ssh_key_copy_failed', {
        type,
        keyName,
        textLength: text.length
      });
    });
  };
  
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setError(null);
      setGeneratedKey(null);
      setNameError('');
      setPublicKeyError('');
      setNameCharCount(0);
      setSelectedRegionForKey('');
      setShowRetry(false);
      
      logInfo('SSH key modal state reset', {
        type,
        wasOpen: isOpen
      });
      
      if (type === 'create') {
        setKeyName('');
        logInfo('SSH key modal prepared for creation', { type });
      }
      if (type === 'import') {
        setKeyName('');
        setPublicKey('');
        logInfo('SSH key modal prepared for import', { type });
      }
    }
  }, [isOpen, type]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (regionKeyDropdownRef.current && !regionKeyDropdownRef.current.contains(event.target)) {
        if (isRegionKeyDropdownOpen) {
          logUserAction('ssh_key_region_dropdown_close_outside_click', {
            type,
            selectedRegion: selectedRegionForKey || 'none'
          });
        }
        setIsRegionKeyDropdownOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isRegionKeyDropdownOpen, selectedRegionForKey, type]);

  const handleClose = () => {
    logUserAction('ssh_key_modal_close', {
      type,
      keyName: keyName || 'empty',
      hasGeneratedKey: !!generatedKey,
      hasError: !!error
    });
    
    logInfo('SSH key modal closed', {
      type,
      keyName: keyName || 'empty',
      hasGeneratedKey: !!generatedKey,
      hasError: !!error
    });
    
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <>
      <style>
        {`
          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: #555555 #2a2a2a;
          }
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #2a2a2a;
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #555555;
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #666666;
          }
        `}
      </style>
      <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4 md:p-2 backdrop-blur-sm">
        <div 
          className="bg-[#121212] rounded-lg w-full max-w-lg md:max-w-md border border-[#333333] shadow-2xl max-h-[90vh] overflow-y-auto custom-scrollbar"
          ref={modalRef}
        >
          <div className="p-4 md:p-5 border-b border-[#222222] flex justify-between items-center min-h-[72px] md:min-h-auto">
            <div className="flex items-center flex-1 mr-4">
              <div className="p-3 md:p-2 rounded-full bg-[#1a1a1a] mr-3 md:mr-3">
                <FaKey className="text-[#e0eacc]" size={20} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg md:text-xl font-semibold text-white">
                  {type === 'create' ? 'Create New SSH Key' : 'Import SSH Key'}
                </h3>
                <p className="text-gray-400 text-sm">
                  {type === 'create' 
                    ? 'Generate a new secure SSH keypair' 
                    : 'Add an existing public key'}
                </p>
              </div>
            </div>
            <button 
              onClick={handleClose}
              className="p-3 md:p-2 rounded-full hover:bg-[#1a1a1a] text-gray-400 hover:text-white transition-colors min-w-[48px] md:min-w-auto min-h-[48px] md:min-h-auto flex items-center justify-center"
            >
              <FaTimes size={18} />
            </button>
          </div>
          
          <div className="p-4 md:p-5">
            {error && (
              <div className="bg-red-900 bg-opacity-20 p-4 rounded border border-red-800 text-red-200 mb-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start flex-1">
                    <FaExclamationTriangle className="mt-1 mr-2 flex-shrink-0" />
                    <div>{sanitizeText(error)}</div>
                  </div>
                  {showRetry && (
                    <button
                      onClick={handleRetry}
                      className="ml-3 px-3 py-1 bg-red-800 hover:bg-red-700 text-white rounded text-sm font-medium transition-colors whitespace-nowrap"
                    >
                      Retry Now
                    </button>
                  )}
                </div>
              </div>
            )}
            
            {generatedKey ? (
              <div>
                {/* Region selector dropdown - only shown if we have region keys */}
                {generatedKey && generatedKey.regionKeys && Object.keys(generatedKey.regionKeys).length > 0 && (
                  <div className="mb-4">
                    <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-3 gap-2">
                      <p className="text-white font-medium">Region Public Key</p>
                      
                      <div className="relative w-full md:w-auto" ref={regionKeyDropdownRef}>
                        <div 
                          className="px-3 py-3 md:py-1.5 border border-[#333333] rounded text-white flex justify-between items-center cursor-pointer min-h-[48px] md:min-h-auto"
                          onClick={toggleRegionKeyDropdown}
                          style={{
                            backgroundColor: '#1d1d1d',
                            borderColor: isRegionKeyDropdownOpen ? '#e0eacc' : '#333333',
                            minWidth: '120px'
                          }}
                        >
                          <span className="text-sm">{sanitizeText(selectedRegionForKey || Object.keys(generatedKey.regionKeys)[0])}</span>
                          <div className="flex items-center gap-1">
                            {Array.from({ length: 3 }).map((_, i) => (
                              <div 
                                key={i}
                                className={`w-1 h-1 bg-[#cad9e3] rounded-full transition-all duration-200 ${
                                  isRegionKeyDropdownOpen ? 'animate-bounce' : ''
                                }`}
                                style={isRegionKeyDropdownOpen ? { 
                                  animationDelay: `${i * 150}ms`,
                                  animationDuration: '1s'
                                } : {}}
                              />
                            ))}
                          </div>
                        </div>
                        {isRegionKeyDropdownOpen && (
                          <div className="absolute z-10 left-0 right-0 md:right-0 md:left-auto mt-1 border border-[#333333] rounded shadow-xl max-h-48 overflow-auto custom-scrollbar bg-[#1d1d1d] md:min-w-[120px]">
                            <div className="py-1">
                              {Object.keys(generatedKey.regionKeys).map((region, index) => (
                                <div 
                                  key={index}
                                  className={`px-3 py-3 md:py-2 cursor-pointer transition-colors min-h-[48px] md:min-h-auto flex items-center ${
                                    selectedRegionForKey === region
                                      ? 'bg-[#252525] text-[#e0eacc]' 
                                      : 'text-gray-200 hover:bg-[#252525] hover:text-[#e0eacc]'
                                  }`}
                                  onClick={() => handleRegionKeySelect(region)}
                                >
                                  <div className="flex items-center">
                                    <div className="w-4 h-2 mr-2">
                                      {getCountryFlag(region.split('-')[0])}
                                    </div>
                                    {sanitizeText(region)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="bg-[#1d1d1d] p-4 rounded-lg border border-[#333333]">
                      {selectedRegionForKey && (
                        <div className="flex items-center mb-2">
                          <div className="w-5 h-3 mr-2">
                            {getCountryFlag(selectedRegionForKey.split('-')[0])}
                          </div>
                          <span className="text-white font-medium text-sm">{sanitizeText(selectedRegionForKey)}</span>
                        </div>
                      )}
                      <pre className="text-gray-300 font-mono text-xs break-all whitespace-pre-wrap max-h-40 overflow-y-auto custom-scrollbar">
                        {selectedRegionForKey ? 
                          `${generatedKey.publicKey.split(' ')[0]} ${generatedKey.publicKey.split(' ')[1]} ${keyName}-${selectedRegionForKey.toLowerCase()}` : 
                          generatedKey.publicKey}
                      </pre>
                      <div className="flex justify-end mt-2">
                        <button
                          onClick={() => handleCopyKey(selectedRegionForKey ? 
                            `${generatedKey.publicKey.split(' ')[0]} ${generatedKey.publicKey.split(' ')[1]} ${keyName}-${selectedRegionForKey.toLowerCase()}` : 
                            generatedKey.publicKey)}
                          className="flex items-center px-3 py-2 md:py-1 text-sm transition-colors min-h-[44px] md:min-h-auto"
                          style={{ 
                            backgroundColor: copied ? '#1c2b22' : '#1c2b22',
                            color: copied ? '#e0eacc' : '#e0eacc',
                            borderRadius: '6px',
                            border: '1px solid #2a3c32'
                          }}
                        >
                          {copied ? (
                            <>
                              <FaCheckCircle className="mr-1" />
                              <span>Copied</span>
                            </>
                          ) : (
                            <>
                              <FaCopy className="mr-1" />
                              <span>Copy</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Download section at bottom */}
                <div className="bg-[#1d1d1d] p-4 md:p-5 rounded-lg border border-[#333333] shadow-sm mb-4">  
                  <div className="flex items-start mb-3">  
                    <FaCheckCircle className="text-[#e0eacc] mr-3 mt-1" size={24} />  
                    <div className="flex-1">  
                      <p className="text-[#e0eacc] font-semibold mb-2 text-sm md:text-base">
                        SSH Key Generated Successfully!
                      </p>  
                      <p className="text-gray-300 text-sm">  
                        Please download your private key now. For security reasons, we won't store it and  
                        you won't be able to download it again.  
                      </p>  
                    </div>  
                  </div>  
                  
                  <div className="mt-4 flex justify-end">     
                    <button       
                      onClick={handleDownloadKey}       
                      className="w-full md:w-auto px-4 py-3 md:py-2 bg-[#e0eacc] hover:bg-[#c5d4a3] text-black rounded text-sm font-medium transition-colors flex items-center justify-center min-h-[48px] md:min-h-auto"       
                    >       
                      <FaDownload className="mr-2" /> Download Private Key     
                    </button>   
                  </div> 
                </div>
              </div>
            ) : (
              <div>
                <div className="mb-5">
                  <label className="block text-white text-sm font-medium mb-3 md:mb-2">Key Name</label>
                  <div className="relative" style={{ marginLeft: '-5%' }}>
                    <input
                      type="text"
                      value={keyName}
                      onChange={handleKeyNameChange}
                      className="w-full border rounded-lg p-4 md:p-3 text-white text-sm min-h-[52px] md:min-h-auto"
                      style={{
                        outline: 'none',
                        backgroundColor: '#1d1d1d',
                        borderColor: nameError ? 'red' : '#e0eacc'
                      }}
                      placeholder="my-ssh-key"
                      autoFocus
                      ref={keyNameInputRef}
                      maxLength={30}
                    />
                  </div>
                  {nameError && (
                    <p className="text-red-400 text-xs mt-2">{sanitizeText(nameError)}</p>
                  )}
                </div>
                
                {type === 'import' && (
                  <div className="mb-5">
                    <label className="block text-white text-sm font-medium mb-3 md:mb-2">Public Key</label>
                    <div className="relative" style={{ marginLeft: '-5%' }}>
                      <textarea
                        ref={publicKeyTextareaRef}
                        value={publicKey}
                        onChange={handlePublicKeyChange}
                        className="w-full border border-[#333333] rounded-lg p-4 md:p-3 text-white text-sm min-h-[140px] md:min-h-[120px] custom-scrollbar"
                        style={{
                          outline: 'none',
                          backgroundColor: '#1d1d1d',
                          borderColor: publicKeyError ? 'red' : '#333333'
                        }}
                        placeholder="ssh-rsa AAAAB3NzaC1yc2EAAAADAQABAAABAQC..."
                      />
                    </div>
                    {publicKeyError && (
                      <p className="text-red-400 text-xs mt-2">{sanitizeText(publicKeyError)}</p>
                    )}
                    <p className="text-gray-400 text-xs mt-2">Paste your SSH public key here</p>
                  </div>
                )}
                
                <div className="flex justify-end mt-6">
                  <button
                    onClick={handleCreate}
                    disabled={loading || !!nameError || (type === 'import' && !!publicKeyError)}
                    className={`w-full md:w-auto px-4 py-3 md:py-2 rounded-lg md:rounded-md font-medium flex items-center justify-center min-h-[48px] md:min-h-auto text-sm ${
                      loading || nameError || (type === 'import' && publicKeyError)
                        ? 'bg-[#444444] cursor-not-allowed text-gray-300' 
                        : 'bg-[#e0eacc] hover:bg-[#c5d4a3] text-black'
                    }`}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-black mr-2"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        {type === 'create' ? (
                          <>
                            <FaKey className="mr-2" /> Create Key
                          </>
                        ) : (
                          <>
                            <FaFileImport className="mr-2" /> Import Key
                          </>
                        )}
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default React.memo(SSHKeyModal);